import { useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, cn } from '@careandkey/shared';

// ── Modal shell ──────────────────────────────────────────────────────────────
export function Modal({ open, onClose, title, children, wide }: {
  open: boolean; onClose: () => void; title: string; children: ReactNode; wide?: boolean;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:p-8" onMouseDown={onClose}>
      <Card className={cn('my-auto w-full p-0', wide ? 'max-w-[680px]' : 'max-w-[480px]')} onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-divider px-5 py-4">
          <h2 className="text-[17px] font-semibold">{title}</h2>
          <button onClick={onClose} aria-label="close" className="rounded-md p-1 text-muted hover:bg-bg hover:text-fg">✕</button>
        </div>
        {children}
      </Card>
    </div>
  );
}

// ── Field wrapper ──────────────────────────────────────────────────────────────
export function Field({ label, required, error, children, hint }: {
  label: string; required?: boolean; error?: string; children: ReactNode; hint?: string;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-xs font-medium text-muted">
        {label}{required && <span className="text-st-overdue-fg"> *</span>}
      </span>
      {children}
      {hint && !error && <span className="text-2xs text-muted">{hint}</span>}
      {error && <span className="text-2xs text-st-overdue-fg">{error}</span>}
    </label>
  );
}

const base = 'h-9 w-full rounded-md border bg-card px-3 text-sm text-fg outline-none focus:border-accent';
export const inputCls = (error?: boolean) => cn(base, error ? 'border-st-overdue-fg' : 'border-border');

export function TextInput(props: React.InputHTMLAttributes<HTMLInputElement> & { error?: boolean }) {
  const { error, className, ...rest } = props;
  return <input {...rest} className={cn(inputCls(error), className)} />;
}

export function Select(props: React.SelectHTMLAttributes<HTMLSelectElement> & { error?: boolean }) {
  const { error, className, children, ...rest } = props;
  return <select {...rest} className={cn(inputCls(error), className)}>{children}</select>;
}

export function Textarea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement> & { error?: boolean }) {
  const { error, className, ...rest } = props;
  return <textarea {...rest} className={cn(base, 'h-auto min-h-[76px] py-2', error ? 'border-st-overdue-fg' : 'border-border', className)} />;
}

// ── Footer actions ─────────────────────────────────────────────────────────────
export function FormActions({ onCancel, busy, submitLabel }: { onCancel: () => void; busy?: boolean; submitLabel?: string }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-end gap-2 border-t border-divider px-5 py-4">
      <Button type="button" variant="secondary" onClick={onCancel}>{t('common.cancel')}</Button>
      <Button type="submit" variant="accent" disabled={busy}>{busy ? t('common.saving') : (submitLabel ?? t('common.save'))}</Button>
    </div>
  );
}

// Map a PostgREST error to a friendly, translated message.
export function dbErrorMessage(t: (k: string, o?: any) => string, error: { code?: string; message?: string } | null): string {
  if (!error) return '';
  if (error.code === '23505') return t('form.errDuplicate');
  if (error.code === '42501' || /row-level security/i.test(error.message ?? '')) return t('form.errForbidden');
  return error.message ?? t('form.errGeneric');
}
